import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import { createFormData } from "../../../utils/form-data/create-form-data.ts"
import type { SubcontractorsFilterOptions } from "../redux/subcontractors.types.ts"
import type { SubcontractorListItem } from "../types/types.ts"
import type {
  CommentSubcontractorParams,
  CreateInviteSubcontractorData,
  CreateSubcontractorData,
  DelayedSubcontractorResponse,
  GetSubcontractorsParams,
  RateSubcontractorData,
  RegisterSubcontractorParams,
  SubcontractorId,
  SubcontractorIdData,
  UpdateSubcontractorData
} from "./services.types.ts"

const DELAY = 500
const BASE_URL = "/subcontractor/subcontractors"
const BASE_URL_DETAILS = (id: SubcontractorId) =>
  `${BASE_URL}/${id && `${id}/`}`

const PATHS = {
  getSubcontractors: `${BASE_URL}/`,
  getSubcontractorsFilterOptions: `${BASE_URL}/filter_options/`,
  createInviteSubcontractor: `${BASE_URL}/invite/`,
  createSubcontractor: `${BASE_URL}/create_subcontractor/`,
  getSubcontractor: BASE_URL_DETAILS,
  updateSubcontractor: BASE_URL_DETAILS,
  deleteSubcontractor: BASE_URL_DETAILS,
  //
  suspendSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}block/`,
  activateSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}unblock/`,
  resendInviteSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}resend_invite/`,
  inviteNoAccess: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}invite_no_access/`,
  commentSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}comment/`,
  rateSubcontractor: (id: SubcontractorId) => `${BASE_URL_DETAILS(id)}rate/`,
  registerSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}register/`
}

export const getSubcontractorsFilterOptions = () => {
  return request.api.get<SubcontractorsFilterOptions, BaseApiResponse<unknown>>(
    PATHS.getSubcontractorsFilterOptions
  )
}

export const getSubcontractors = (params?: GetSubcontractorsParams) => {
  return request.api.get<
    BasePagination<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.getSubcontractors, params)
}

/** Sidebar badge: how many subcontractors have a supplied document that is
 * expiring soon or expired. Live-derived on the server, never a stored count. */
export const getSubcontractorComplianceAlertCount = () => {
  return request.api.get<{ count: number }, BaseApiResponse<unknown>>(
    `${BASE_URL}/compliance_alert_count/`
  )
}

export const getSubcontractorDetails = (id: SubcontractorId) => {
  return request.api.get<SubcontractorListItem, BaseApiResponse<unknown>>(
    PATHS.getSubcontractor(id)
  )
}

export const createInviteSubcontractor = (
  data: CreateInviteSubcontractorData
) => {
  // An invitation can now carry the same optional profile as a direct add,
  // including document uploads, which need multipart — a plain invite stays
  // JSON, unchanged from before.
  const hasFile = Object.values(data).some((value) => value instanceof File)

  if (hasFile) {
    return request.api.post<
      BaseApiResponse<SubcontractorListItem>,
      BaseApiResponse<unknown>
    >(PATHS.createInviteSubcontractor, createFormData(data), {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }

  return request.api.post<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.createInviteSubcontractor, data)
}

export const updateSubcontractor = ({
  id,
  ...newInfo
}: UpdateSubcontractorData) => {
  // Only a re-uploaded W-9 (Stage 3) needs multipart here — everything else
  // a plain edit sends stays JSON, unchanged from before.
  const hasFile = Object.values(newInfo).some((value) => value instanceof File)

  if (hasFile) {
    // createFormData skips null, which is how a cleared field travels as JSON;
    // in multipart an empty string is what DRF reads back as null.
    const clearedAsEmpty = Object.fromEntries(
      Object.entries(newInfo).map(([key, value]) => [key, value ?? ""])
    )
    return request.api.patch<
      BaseApiResponse<SubcontractorListItem>,
      BaseApiResponse<unknown>
    >(PATHS.updateSubcontractor(id), createFormData(clearedAsEmpty), {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }

  return request.api.patch<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.updateSubcontractor(id), newInfo)
}

export const deleteSubcontractor = (id: SubcontractorId) => {
  return request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.deleteSubcontractor(id)
  )
}

export const suspendSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.suspendSubcontractor(id)
  )
}

export const activateSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.activateSubcontractor(id)
  )
}

export const resendInviteSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.resendInviteSubcontractor(id)
  )
}

export const inviteNoAccessSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.inviteNoAccess(id)
  )
}

export const rateSubcontractor = ({ id, ...rest }: RateSubcontractorData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.rateSubcontractor(id),
    rest
  )
}

export const createSubcontractor = (data: CreateSubcontractorData) => {
  // Carries the mandatory compliance-document files (Stage 1) and the W-9
  // (Stage 3), so this posts multipart rather than JSON.
  return request.api.post<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.createSubcontractor, createFormData(data), {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

export const registerSubcontractor = (params: RegisterSubcontractorParams) => {
  return new Promise<DelayedSubcontractorResponse>((resolve) => {
    setTimeout(() => {
      resolve({ ok: true, data: {} } as DelayedSubcontractorResponse)
    }, DELAY)
  })
}

export const commentSubcontractor = (params: CommentSubcontractorParams) => {
  return new Promise<DelayedSubcontractorResponse>((resolve) => {
    setTimeout(() => {
      resolve({ ok: true, data: {} } as DelayedSubcontractorResponse)
    }, DELAY)
  })
}
